/**
 * Onboarding flow state.
 *
 * Keeps step progression, environment snapshot, install job tracking, and
 * token-verification state. Resume-friendly: on app reload, the store's
 * `init()` re-fetches the environment so we land the user on the right
 * step even if they closed the app mid-install.
 */

import { create } from "zustand";
import type {
  EnvironmentReport,
  InstallKind,
  InstallStatus,
  TokenVerifyResponse,
} from "@/api/onboarding";
import { onboardingApi } from "@/api/onboarding";
import { apiFetch } from "@/api/client";
import { useSettingsStore } from "@/stores/settingsStore";

export type OnboardingStepId =
  | "welcome"
  | "env"
  | "install"
  | "provision"
  | "services"
  | "done";

/** The sequence of visible steps in the dots indicator. */
export const STEP_ORDER: OnboardingStepId[] = [
  "welcome",
  "env",
  "install",
  "provision",
  "services",
  "done",
];

/** Shared service credentials onboarding may still collect (admins only). */
export type ServiceKey = "opensearch_username" | "opensearch_password";

// Perplexity is no longer collected at onboarding — web research falls back
// to OpenRouter's ``sonar`` models, so the OpenRouter key already covers it.
// The Nbyula gateway key left too (2026-07-29): it is per-employee and minted
// server-to-server, so asking anyone to paste one was both impossible for them
// and wrong — a typed key would make them act as whoever it belongs to.
export const SERVICE_KEYS: ServiceKey[] = [
  "opensearch_username",
  "opensearch_password",
];

/** Per-field verification state (mirrors TokenValidationState shape). */
export type FieldValidation =
  | { kind: "idle" }
  | { kind: "verifying" }
  | { kind: "verified"; message: string }
  | { kind: "failed"; message: string };

export type TokenValidationState =
  | { kind: "idle" }
  | { kind: "typing" }
  | { kind: "shape_invalid"; message: string }
  | { kind: "verifying" }
  | { kind: "verified"; message: string }
  | { kind: "failed"; message: string };

/** What ``POST /provision/refresh`` reports back — booleans, never secrets. */
export interface ProvisionStatus {
  openrouter: boolean;
  claude: boolean;
  opensearch: boolean;
  gateway: boolean;
  filled: string[];
  /** Why the gateway key is missing, when it is. Drives the on-screen advice. */
  gateway_reason?:
    | "role_not_allowed"
    | "no_account"
    | "provisioning_disabled"
    | "unavailable"
    | null;
}

/**
 * What to tell the user when provisioning didn't complete. The gateway's
 * refusal reasons are the interesting ones: a role problem is not something a
 * retry can fix, so say who can fix it instead of inviting them to hammer the
 * button.
 */
export function provisionMessage(status: ProvisionStatus): string {
  if (!status.gateway) {
    if (status.gateway_reason === "role_not_allowed") {
      return (
        "Your Nbyula account doesn't have a role that permits data access " +
        "(only ADMIN and MODERATOR do). Ask the engineering team to update " +
        "your role, then hit Recheck — no need to restart."
      );
    }
    if (status.gateway_reason === "no_account") {
      return (
        "We couldn't find your Nbyula account. Ask the engineering team to " +
        "check your email is registered, then hit Recheck."
      );
    }
    if (status.gateway_reason === "provisioning_disabled") {
      return (
        "Nbyula data access isn't being handed out yet — the gateway's " +
        "provisioning is switched off. This is an engineering setup step, not " +
        "something about your account; ask them to enable it, then hit Recheck."
      );
    }
    if (!status.openrouter) {
      return "We couldn't set up your workspace access. This is usually temporary.";
    }
    return "We couldn't set up your Nbyula data access. This is usually temporary.";
  }
  return "We couldn't set up model access for you automatically.";
}

/**
 * State of the provisioning step. ``done`` means model access is usable;
 * ``unavailable`` means provisioning ran but produced no model credential
 * (management key down, no cloud credential yet) — the user is offered a
 * retry and a bring-your-own-key escape hatch rather than being walled in.
 */
export type ProvisionState =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "done"; status: ProvisionStatus }
  | { kind: "unavailable"; status: ProvisionStatus | null; message: string };

interface InstallTracking {
  jobId: string;
  kind: InstallKind;
  status: InstallStatus;
  log: string[];
  cursor: number;
  error: string | null;
  durationSeconds: number;
}

interface OnboardingState {
  step: OnboardingStepId;
  environment: EnvironmentReport | null;
  envError: string | null;
  envLoading: boolean;

  /** Order of installers still to run (populated from env report). */
  installQueue: InstallKind[];
  activeInstall: InstallTracking | null;

  token: string;
  tokenValidation: TokenValidationState;

  /** Credential provisioning (ADR-0014): what the app fetched for itself. */
  provisionState: ProvisionState;

  /** Service credentials collected on the Services step. */
  services: Record<ServiceKey, string>;
  servicesValidation: Record<ServiceKey, FieldValidation>;

  /** Going forward = true, going backward = false. Drives transition direction. */
  transitionForward: boolean;

  // Actions
  goTo: (step: OnboardingStepId, forward?: boolean) => void;
  goNext: () => void;
  goBack: () => void;

  refreshEnvironment: () => Promise<void>;
  planInstalls: () => void;

  startNextInstall: () => Promise<void>;
  pollActiveInstall: () => Promise<void>;
  cancelActiveInstall: () => Promise<void>;

  setToken: (value: string) => void;
  verifyToken: () => Promise<TokenVerifyResponse | null>;

  /** Fetch/mint this install's credentials from the org (ADR-0014). */
  provision: () => Promise<void>;

  setServiceValue: (key: ServiceKey, value: string) => void;
  verifyService: (key: ServiceKey) => Promise<{ verified: boolean; message: string }>;

  reset: () => void;
}

const initialServices: Record<ServiceKey, string> = {
  // Default username matches the production OpenSearch cluster — overridable.
  opensearch_username: "admin",
  opensearch_password: "",
};

const idleValidation: Record<ServiceKey, FieldValidation> = {
  opensearch_username: { kind: "idle" },
  opensearch_password: { kind: "idle" },
};

const initial: Pick<
  OnboardingState,
  | "step"
  | "environment"
  | "envError"
  | "envLoading"
  | "installQueue"
  | "activeInstall"
  | "token"
  | "tokenValidation"
  | "provisionState"
  | "services"
  | "servicesValidation"
  | "transitionForward"
> = {
  step: "welcome",
  environment: null,
  envError: null,
  envLoading: false,
  installQueue: [],
  activeInstall: null,
  token: "",
  tokenValidation: { kind: "idle" },
  provisionState: { kind: "idle" },
  services: { ...initialServices },
  servicesValidation: { ...idleValidation },
  transitionForward: true,
};

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
  ...initial,

  goTo: (step, forward = true) =>
    set((s) => ({
      step,
      transitionForward:
        forward ?? STEP_ORDER.indexOf(step) > STEP_ORDER.indexOf(s.step),
    })),

  goNext: () => {
    const { step } = get();
    const idx = STEP_ORDER.indexOf(step);
    if (idx < STEP_ORDER.length - 1) {
      set({ step: STEP_ORDER[idx + 1], transitionForward: true });
    }
  },

  goBack: () => {
    const { step } = get();
    const idx = STEP_ORDER.indexOf(step);
    if (idx > 0) {
      set({ step: STEP_ORDER[idx - 1], transitionForward: false });
    }
  },

  refreshEnvironment: async () => {
    set({ envLoading: true, envError: null });
    try {
      const env = await onboardingApi.getEnvironment();
      set({ environment: env, envLoading: false });
      get().planInstalls();
    } catch (e) {
      set({
        envError: (e as Error).message,
        envLoading: false,
      });
    }
  },

  planInstalls: () => {
    const env = get().environment;
    if (!env) return;
    const queue: InstallKind[] = [];

    // Git first on Windows — Claude Code shells out to git for several
    // built-in tools, and the Anthropic docs explicitly require it for
    // native Windows setups. Installing claude-cli before git can leave
    // the runtime in a half-broken state on first launch.
    if (env.git && !env.git.installed) queue.push("git_for_windows");

    // The `claude` CLI is the chat *runtime* — claude-agent-sdk spawns it
    // and we route it to OpenRouter via the local LiteLLM proxy (no Claude
    // account needed). Install a system copy when one isn't already present
    // so the runtime is reliable across platforms.
    if (env.claude_cli.source !== "system") queue.push("claude_cli");

    set({ installQueue: queue });
  },

  startNextInstall: async () => {
    const queue = get().installQueue;
    const kind = queue[0];
    if (!kind) return;

    const job = await onboardingApi.startInstall(kind);
    set({
      activeInstall: {
        jobId: job.id,
        kind,
        status: job.status,
        log: [],
        cursor: 0,
        error: null,
        durationSeconds: 0,
      },
    });
  },

  pollActiveInstall: async () => {
    const active = get().activeInstall;
    if (!active) return;
    if (active.status === "succeeded" || active.status === "failed" || active.status === "cancelled") {
      return;
    }
    try {
      const res = await onboardingApi.pollInstall(active.jobId, active.cursor);
      set((s) => {
        if (!s.activeInstall || s.activeInstall.jobId !== active.jobId) return s;
        const updated: InstallTracking = {
          ...s.activeInstall,
          status: res.status,
          cursor: res.cursor,
          log: res.lines.length ? [...s.activeInstall.log, ...res.lines] : s.activeInstall.log,
          error: res.error_message ?? null,
          durationSeconds: res.duration_seconds,
        };

        // On success, drop this kind from the queue and (optionally)
        // re-detect to update the environment snapshot so any
        // downstream logic sees the tool as present.
        let nextQueue = s.installQueue;
        if (res.status === "succeeded") {
          nextQueue = s.installQueue.filter((k) => k !== active.kind);
        }

        return { activeInstall: updated, installQueue: nextQueue };
      });

      // If install just finished successfully and there's more in the
      // queue, move on to the next one automatically after a short beat.
      if (res.status === "succeeded" && get().installQueue.length > 0) {
        setTimeout(() => {
          get().startNextInstall().catch(() => {});
        }, 900);
      }

      // If everything is done, refresh the environment snapshot so
      // the rest of the flow sees the tools as present.
      if (res.status === "succeeded" && get().installQueue.length === 0) {
        await get().refreshEnvironment();
      }
    } catch (e) {
      set((s) =>
        s.activeInstall
          ? { activeInstall: { ...s.activeInstall, error: (e as Error).message } }
          : s,
      );
    }
  },

  cancelActiveInstall: async () => {
    const active = get().activeInstall;
    if (!active) return;
    try {
      await onboardingApi.cancelInstall(active.jobId);
    } catch {
      /* If the server already ended it, that's fine. */
    }
    set((s) =>
      s.activeInstall
        ? { activeInstall: { ...s.activeInstall, status: "cancelled" } }
        : s,
    );
  },

  provision: async () => {
    // The org already pays for model access, OpenSearch and the MCP gateway —
    // so the app fetches its own credentials instead of asking the user for
    // keys they'd have to go buy (ADR-0014). Cloud mints; desktop pulls from
    // the cloud with its stored credential. Never throws: a failure here is a
    // state the step renders (retry + bring-your-own), not an exception.
    set({ provisionState: { kind: "running" } });
    try {
      const status = await apiFetch<ProvisionStatus>("/provision/refresh", {
        method: "POST",
      });
      // Re-read settings so the steps AFTER this one see what just landed —
      // otherwise Services still believes the credentials it just received are
      // missing and asks for them.
      if (status.filled.length > 0 || status.openrouter) {
        await useSettingsStore.getState().loadSettings();
      }
      // Setup completes only when BOTH are in hand: without model access
      // nothing runs, and without a gateway key the agent has no Nbyula data.
      // Anything short of that keeps the user here with a Recheck.
      set({
        provisionState:
          status.openrouter && status.gateway
            ? { kind: "done", status }
            : { kind: "unavailable", status, message: provisionMessage(status) },
      });
    } catch (e) {
      set({
        provisionState: {
          kind: "unavailable",
          status: null,
          message: (e as Error).message || "Couldn't reach OmniWerk.",
        },
      });
    }
  },

  setToken: (value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      set({ token: value, tokenValidation: { kind: "idle" } });
      return;
    }
    set({ token: value, tokenValidation: { kind: "typing" } });
  },

  verifyToken: async () => {
    // Despite the legacy name, ``token`` now holds the OpenRouter API key —
    // the single model-provider credential new installs need. Verified via
    // the same ``/settings/verify`` endpoint the rest of the app uses.
    const { token } = get();
    const value = token.trim();
    if (!value) {
      set({ tokenValidation: { kind: "idle" } });
      return null;
    }

    // Light client-side shape check — OpenRouter keys start with ``sk-or-``.
    if (!value.startsWith("sk-or-")) {
      const message = 'OpenRouter keys start with "sk-or-".';
      set({ tokenValidation: { kind: "shape_invalid", message } });
      return { verified: false, looks_like_token: false, message };
    }

    set({ tokenValidation: { kind: "verifying" } });
    try {
      const res = await apiFetch<{ verified: boolean; message: string }>(
        "/settings/verify",
        {
          method: "POST",
          body: JSON.stringify({ key: "openrouter_api_key", value }),
        },
      );
      set({
        tokenValidation: res.verified
          ? { kind: "verified", message: res.message }
          : { kind: "failed", message: res.message },
      });
      return { ...res, looks_like_token: true };
    } catch (e) {
      set({
        tokenValidation: {
          kind: "failed",
          message: (e as Error).message,
        },
      });
      return null;
    }
  },

  setServiceValue: (key, value) => {
    // Strip whitespace from secrets that get pasted from terminals (newlines,
    // zero-width spaces, etc.) — but only for the key fields, not username.
    const cleaned = key === "opensearch_username" ? value : value.replace(/\s+/g, "");
    set((s) => ({
      services: { ...s.services, [key]: cleaned },
      servicesValidation: { ...s.servicesValidation, [key]: { kind: "idle" } },
    }));
  },

  verifyService: async (key) => {
    const { services } = get();
    const value = services[key].trim();
    if (!value) {
      const result = { verified: false, message: "Enter a value first." };
      set((s) => ({
        servicesValidation: {
          ...s.servicesValidation,
          [key]: { kind: "failed", message: result.message },
        },
      }));
      return result;
    }

    // Username has no separate verify endpoint — it's used as context for
    // the password check. Treat any non-empty value as locally verified so
    // the Continue gate doesn't block on it.
    if (key === "opensearch_username") {
      set((s) => ({
        servicesValidation: {
          ...s.servicesValidation,
          [key]: { kind: "verified", message: "Looks good." },
        },
      }));
      return { verified: true, message: "Looks good." };
    }

    set((s) => ({
      servicesValidation: {
        ...s.servicesValidation,
        [key]: { kind: "verifying" },
      },
    }));

    // Pass sibling fields as context so the OpenSearch password check
    // can use the username/host/port the user just entered.
    const context: Record<string, string> = {};
    for (const [k, v] of Object.entries(services)) {
      if (v.trim()) context[k] = v.trim();
    }

    try {
      const res = await apiFetch<{ verified: boolean; message: string }>(
        "/settings/verify",
        {
          method: "POST",
          body: JSON.stringify({ key, value, context }),
        },
      );
      set((s) => ({
        servicesValidation: {
          ...s.servicesValidation,
          [key]: res.verified
            ? { kind: "verified", message: res.message }
            : { kind: "failed", message: res.message },
        },
      }));
      return res;
    } catch (e) {
      const message = (e as Error).message;
      set((s) => ({
        servicesValidation: {
          ...s.servicesValidation,
          [key]: { kind: "failed", message },
        },
      }));
      return { verified: false, message };
    }
  },

  reset: () => set(initial),
}));
